import React from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { SectionCard, PrimaryButton, SecondaryButton, GhostButton } from "@/components/ds";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import TeamsPhoneSetupWizard from "./TeamsPhoneSetupWizard";

const TEAMS_COLOR = "#0078D4";

interface TeamsPhoneConfigSummary {
  configured: boolean;
  tenantId?: string;
  applicationInstanceObjectId?: string;
  assignedPhoneNumber?: string;
  graphSubscriptionActive?: boolean;
}

export default function TeamsPhone() {
  const api = useApi();
  const [tab, setTab] = React.useState<"overview" | "activity">("overview");
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [config, setConfig] = React.useState<TeamsPhoneConfigSummary | null>(
    null
  );
  const [loadingConfig, setLoadingConfig] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");

  const loadConfig = React.useCallback(async () => {
    setLoadingConfig(true);
    try {
      const res = await api.messaging.get("TeamsPhone/Config");
      const data = await res.json();
      setConfig(data);
      setLoadError("");
    } catch (err) {
      setLoadError(
        `Failed to load configuration: ${err instanceof Response ? (err as Response).status : String(err)}`
      );
      setConfig(null);
    } finally {
      setLoadingConfig(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const tabsJSX = (
    <>
      <IntegrationTab
        active={tab === "overview"}
        onClick={() => setTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={tab === "activity"}
        onClick={() => setTab("activity")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Microsoft Teams Phone" tabs={tabsJSX}>
      {tab === "overview" && (
        <div className="space-y-4">
          <SectionCard title="Teams Phone CTI Integration">
            <div className="text-zinc-500 text-sm leading-relaxed mb-4">
              Connect Microsoft Teams Phone System (Direct Routing or Calling
              Plans) to get a full customer screen pop on every inbound call.
              When a call arrives, Velaro automatically searches your connected
              CRM, service, and e-commerce integrations and surfaces matching
              customer data in the agent sidebar — before they answer.
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                Screen pop
              </span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">
                Call logging
              </span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">
                Hold / resume / transfer
              </span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">
                Microsoft 365
              </span>
            </div>

            {loadingConfig && (
              <div className="text-sm text-zinc-500">
                Loading configuration...
              </div>
            )}
            {loadError && (
              <div
                role="alert"
                className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800 mb-4"
              >
                {loadError}
              </div>
            )}

            {!loadingConfig && !loadError && (
              <div className="p-4 rounded-xl bg-zinc-50 border border-zinc-200 text-sm">
                {config?.configured ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-semibold text-zinc-800 mb-1">
                        Configured
                      </div>
                      <div className="text-xs text-zinc-500">
                        Tenant {config.tenantId} - Graph subscription{" "}
                        <span
                          className={
                            config.graphSubscriptionActive
                              ? "text-green-700 font-medium"
                              : "text-amber-700 font-medium"
                          }
                        >
                          {config.graphSubscriptionActive
                            ? "active"
                            : "not active"}
                        </span>
                      </div>
                    </div>
                    <SecondaryButton onClick={() => setWizardOpen(true)}>
                      Edit setup
                    </SecondaryButton>
                  </div>
                ) : (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <strong>Setup required.</strong> Teams Phone uses the
                      Microsoft Graph Communications API and requires a short
                      setup walkthrough (Microsoft admin consent + a Teams Admin
                      Center resource account, both on the customer's own
                      tenant).
                    </div>
                    <PrimaryButton onClick={() => setWizardOpen(true)}>
                      Start setup
                    </PrimaryButton>
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          {wizardOpen && (
            <TeamsPhoneSetupWizard
              onClose={() => setWizardOpen(false)}
              onSaved={() => {
                setWizardOpen(false);
                loadConfig();
              }}
            />
          )}

          <SectionCard
            title="What you get"
            description="All features use the same screen pop provider system as Nextiva — your CRM integrations are automatically available."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  icon: "Phone",
                  title: "Inbound call screen pop",
                  desc: "When a Teams Phone call arrives, Velaro searches all your integrations (HubSpot, Salesforce, ServiceTitan, etc.) and shows the caller's profile in the agent sidebar."
                },
                {
                  icon: "Link",
                  title: "Direct Routing + Calling Plans",
                  desc: "Works with both SBC-based Direct Routing and Microsoft-hosted Calling Plans. No third-party PBX required."
                },
                {
                  icon: "FileText",
                  title: "Post-call activity logging",
                  desc: "When a call ends, Velaro creates a call note on the matched contact in your CRM — Dynamics 365, HubSpot, Salesforce, or Velaro CRM."
                },
                {
                  icon: "Phone",
                  title: "In-call agent controls",
                  desc: "Hold, resume, transfer, and end an in-progress Teams Phone call from the agent's Velaro sidebar."
                }
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex gap-3 p-3 rounded-xl border border-zinc-100 bg-zinc-50"
                >
                  <span className="shrink-0 text-zinc-600">
                    <IconByName name={item.icon} className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 mb-1">
                      {item.title}
                    </div>
                    <div className="text-xs text-zinc-500 leading-relaxed">
                      {item.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="How screen pop works"
            description="Teams Phone uses the same automatic lookup system as all Velaro phone integrations."
          >
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              Every enabled CRM and service integration on your site is
              automatically checked when a call arrives. The lookup runs in
              parallel across all of them — HubSpot, Salesforce, Dynamics 365,
              ServiceTitan, ServiceNow, VAST, and more. The first match wins and
              appears in the agent sidebar.
            </div>
            <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200 text-xs text-zinc-500">
              See the{" "}
              <Link
                to="/integrations/nextiva"
                className="text-(--ds-primary) underline"
              >
                Nextiva integration page
              </Link>{" "}
              for the full list of integrations with screen pop support and a
              detailed explanation of how the lookup works.
            </div>
          </SectionCard>

          <SectionCard title="Setup process">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: "1",
                  title: "Grant tenant consent",
                  desc: "Velaro's existing Graph app registration is reused for every customer — no per-customer app registration. The Start Setup wizard walks through the consent link."
                },
                {
                  step: "2",
                  title: "Create the resource account",
                  desc: "Your Microsoft 365 admin creates a Teams Phone resource account and assigns it a number in Teams Admin Center — this step happens on your tenant and can't be automated."
                },
                {
                  step: "3",
                  title: "Configure & verify in Velaro",
                  desc: "Enter the tenant ID and resource account ID into the wizard, then run the built-in connection test before going live."
                }
              ].map((s) => (
                <div key={s.step} className="flex gap-3">
                  <span
                    className="shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5"
                    style={{ backgroundColor: TEAMS_COLOR }}
                  >
                    {s.step}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 mb-1">
                      {s.title}
                    </div>
                    <div className="text-xs text-zinc-500 leading-relaxed">
                      {s.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {tab === "activity" && (
        <IntegrationActivityLog integrationSlug="TeamsPhone" />
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingTeamsPhone() {
  const navigate = useNavigate();
  return (
    <GhostButton
      onClick={() => navigate("/integrations")}
      className="!text-zinc-500 hover:!text-zinc-700"
    >
      ← Integrations
    </GhostButton>
  );
}
